import type {
  AssignmentId,
  ISODateTime,
  Money,
  ResourceId,
  TaskId,
} from '../domain/branded.js';
import { money } from '../domain/branded.js';
import type { Project } from '../domain/project.js';
import type { Resource } from '../domain/resource.js';
import type { Task } from '../domain/task.js';
import type { BaselineAssignmentSnapshot, BaselineNumber, BaselineTaskSnapshot } from '../domain/baseline.js';
import type { TimephasedCostSeries, TimephasedGranularity } from '../domain/timephased.js';
import type { Diagnostic } from '../domain/errors.js';
import { EntityNotFoundError } from '../domain/errors.js';
import type { EvmOptions, EvmResult } from '../domain/evm.js';
import { intersectWorkingCalendars, resolveCalendar, type ResolvedCalendar } from './calendar-engine.js';
import { assignmentLaborCostSegments, computeAssignmentCostRaw, accrueFixedCostActual } from './cost-assignment.js';
import { distributeCost, mergeCostSeries } from './cost-timephased.js';
import { computeEarnedValue as computeEarnedValueImpl } from './evm-engine.js';

/**
 * 成本引擎契約。實作者：cost agent。
 */

export interface CostOptions {
  /** 時間分佈的粒度；未提供則不產生 timephased 資料。 */
  readonly timephasedGranularity?: TimephasedGranularity;
  /** 比較用的基準線；未提供則不計算 baseline 差異。 */
  readonly baselineNumber?: BaselineNumber;
  /** 是否計入加班費率。 */
  readonly includeOvertime?: boolean;
  /** 狀態日；影響 remaining / actual 的切分。未提供則採用 `Project.statusDate`。 */
  readonly statusDate?: ISODateTime;
}

export interface CostInput {
  readonly project: Project;
  readonly options?: CostOptions;
}

/** 成本三分法：實際 + 剩餘 = 總計。 */
export interface CostBreakdown {
  readonly cost: Money;
  readonly actualCost: Money;
  readonly remainingCost: Money;
  /** 對應基準線的預算成本；無基準線時為 null。 */
  readonly baselineCost: Money | null;
  /** cost − baselineCost；無基準線時為 null。 */
  readonly variance: Money | null;
}

export interface TaskCost extends CostBreakdown {
  readonly taskId: TaskId;
  readonly fixedCost: Money;
  /** 不含 fixedCost 的資源成本。 */
  readonly resourceCost: Money;
}

export interface ResourceCost extends CostBreakdown {
  readonly resourceId: ResourceId;
  readonly standardCost: Money;
  readonly overtimeCost: Money;
  readonly costPerUseTotal: Money;
}

export interface AssignmentCost extends CostBreakdown {
  readonly assignmentId: AssignmentId;
  readonly taskId: TaskId;
  readonly resourceId: ResourceId;
  readonly timephased: TimephasedCostSeries | null;
}

export interface CostResult {
  readonly total: CostBreakdown;
  readonly byTask: readonly TaskCost[];
  readonly byResource: readonly ResourceCost[];
  readonly byAssignment: readonly AssignmentCost[];
  /** 專案層級的累積成本曲線（依日期遞增）。 */
  readonly cumulativeCurve: TimephasedCostSeries;
  readonly diagnostics: readonly Diagnostic[];
}

// ---------------------------------------------------------------------------
// 內部工具
// ---------------------------------------------------------------------------

const num = (m: Money): number => m as number;

function combineBreakdown(
  cost: number,
  actualCost: number,
  baselineCost: number | null,
): CostBreakdown {
  return {
    cost: money(cost),
    actualCost: money(actualCost),
    remainingCost: money(cost - actualCost),
    baselineCost: baselineCost === null ? null : money(baselineCost),
    variance: baselineCost === null ? null : money(cost - baselineCost),
  };
}

/**
 * 計算專案全部成本。
 *
 * Work resource rates are resolved per effective-dated selected rate table;
 * sparse B-E tables inherit A and legacy fields remain A's baseline.
 */
export function computeCost(input: CostInput): CostResult {
  const { project, options } = input;
  const statusDate = options?.statusDate ?? project.statusDate ?? undefined;
  const includeOvertime = options?.includeOvertime ?? true;
  const diagnostics: Diagnostic[] = [];

  const tasksById = new Map<string, Task>(project.tasks.map((t) => [t.id as string, t]));
  const resourcesById = new Map<string, Resource>(project.resources.map((r) => [r.id as string, r]));
  const calendarsById = new Map(project.calendars.map((c) => [c.id as string, c]));

  const resolvedCalendarCache = new Map<string, ResolvedCalendar>();
  const resolveCal = (calendarId: string): ResolvedCalendar => {
    const cached = resolvedCalendarCache.get(calendarId);
    if (cached !== undefined) return cached;
    const cal = calendarsById.get(calendarId);
    if (cal === undefined) throw new EntityNotFoundError('CALENDAR_NOT_FOUND', calendarId);
    const resolved = resolveCalendar(cal, calendarsById);
    resolvedCalendarCache.set(calendarId, resolved);
    return resolved;
  };
  const assignmentsByTaskId = new Map<string, readonly import('../domain/assignment.js').Assignment[]>();
  for (const assignment of project.assignments) {
    const key = assignment.taskId as string;
    assignmentsByTaskId.set(key, [...(assignmentsByTaskId.get(key) ?? []), assignment]);
  }
  const assignmentCalendarFor = (task: Task): ResolvedCalendar => {
    const projectCalendar = resolveCal(project.defaultCalendarId as string);
    const taskCalendar = task.calendarId === null ? projectCalendar : resolveCal(task.calendarId as string);
    if (task.ignoreResourceCalendar) return taskCalendar;
    const calendars = [taskCalendar];
    for (const assignment of assignmentsByTaskId.get(task.id as string) ?? []) {
      const resource = resourcesById.get(assignment.resourceId as string);
      if (resource?.type === 'Work') calendars.push(resource.calendarId === null ? projectCalendar : resolveCal(resource.calendarId as string));
    }
    return intersectWorkingCalendars(calendars);
  };

  const baseline =
    options?.baselineNumber !== undefined
      ? project.baselines.find((b) => b.number === options.baselineNumber)
      : undefined;
  if (options?.baselineNumber !== undefined && baseline === undefined) {
    throw new EntityNotFoundError('BASELINE_NOT_FOUND', String(options.baselineNumber));
  }
  // 結構性防護：baseline.tasks/assignments 可能不是陣列、或含 null/非物件項目
  // （通常來自損壞的 .bproj 檔案，不是 captureBaseline() 產生的資料——那個型別
  // 有保證）。這裡只做最低限度防護，讓 computeCost() 不會因為結構壞掉而直接
  // 拋 TypeError；深度欄位驗證不是這裡的職責。
  const sanitizeSnapshotArray = (raw: unknown): readonly object[] => {
    if (!Array.isArray(raw)) {
      diagnostics.push({
        severity: 'Warning',
        code: 'COST_BASELINE_SNAPSHOT_INCOMPLETE',
        message: 'Baseline snapshot list is not an array (malformed baseline data); treated as empty.',
      });
      return [];
    }
    const clean = raw.filter((item): item is object => item !== null && typeof item === 'object');
    if (clean.length !== raw.length) {
      diagnostics.push({
        severity: 'Warning',
        code: 'COST_BASELINE_SNAPSHOT_INCOMPLETE',
        message: `Baseline snapshot list contained ${String(raw.length - clean.length)} null/non-object item(s) (malformed baseline data); they were skipped.`,
      });
    }
    return clean;
  };
  const safeBaselineAssignments =
    baseline === undefined ? [] : (sanitizeSnapshotArray(baseline.assignments) as readonly BaselineAssignmentSnapshot[]);
  const safeBaselineTasks =
    baseline === undefined ? [] : (sanitizeSnapshotArray(baseline.tasks) as readonly BaselineTaskSnapshot[]);
  const baselineAssignmentById =
    baseline === undefined ? null : new Map(safeBaselineAssignments.map((a) => [a.assignmentId as string, a]));
  const baselineTaskById =
    baseline === undefined ? null : new Map(safeBaselineTasks.map((t) => [t.taskId as string, t]));

  // ---- Assignment level -----------------------------------------------
  const byAssignment: AssignmentCost[] = [];
  const assignmentTimephasedByTaskId = new Map<string, TimephasedCostSeries[]>();

  for (const assignment of project.assignments) {
    const task = tasksById.get(assignment.taskId as string);
    if (task === undefined) throw new EntityNotFoundError('TASK_NOT_FOUND', assignment.taskId as string);
    const resource = resourcesById.get(assignment.resourceId as string);
    if (resource === undefined) throw new EntityNotFoundError('RESOURCE_NOT_FOUND', assignment.resourceId as string);
    if (resource.isBudget) continue;

    const calendar = assignmentCalendarFor(task);
    const raw = computeAssignmentCostRaw(assignment, resource, includeOvertime, task, statusDate, calendar);

    const baselineSnapshot = baselineAssignmentById?.get(assignment.id as string) ?? null;
    if (baseline !== undefined && baselineSnapshot === null) {
      diagnostics.push({
        severity: 'Info',
        code: 'BASELINE_ASSIGNMENT_MISSING',
        message: `Assignment ${assignment.id as string} has no snapshot in baseline ${baseline.number}.`,
        taskId: task.id,
        resourceId: resource.id,
      });
    }

    let timephased: TimephasedCostSeries | null = null;
    if (options?.timephasedGranularity !== undefined) {
      const granularity = options.timephasedGranularity;
      const labourTotal = money((raw.standardCost as number) + (raw.overtimeCost as number));
      const labour = resource.accrueAt === 'Prorated'
        ? assignmentLaborCostSegments(assignment, resource, includeOvertime, calendar)
          .flatMap((segment) => distributeCost({ start: segment.start, finish: segment.finish, totalCost: segment.cost, accrueAt: 'Prorated', granularity, calendar }))
        : distributeCost({ start: assignment.start, finish: assignment.finish, totalCost: labourTotal, accrueAt: resource.accrueAt, granularity, calendar });
      const perUse = (raw.costPerUseTotal as number) === 0 ? [] : distributeCost({ start: assignment.start, finish: assignment.finish, totalCost: raw.costPerUseTotal, accrueAt: resource.accrueAt, granularity, calendar });
      const merged = mergeCostSeries([labour, perUse]);
      const byPeriod = new Map<string, { start: ISODateTime; end: ISODateTime; value: number }>();
      for (const slice of merged) {
        const key = `${slice.start as string}|${slice.end as string}`;
        const current = byPeriod.get(key) ?? { start: slice.start, end: slice.end, value: 0 };
        current.value += slice.valueCost as number;
        byPeriod.set(key, current);
      }
      timephased = [...byPeriod.values()].sort((a, b) => a.start < b.start ? -1 : a.start > b.start ? 1 : 0)
        .map((slice) => ({ kind: 'Cost' as const, start: slice.start, end: slice.end, valueCost: money(slice.value) }));
      const list = assignmentTimephasedByTaskId.get(task.id as string) ?? [];
      list.push(timephased);
      assignmentTimephasedByTaskId.set(task.id as string, list);
    }

    byAssignment.push({
      assignmentId: assignment.id,
      taskId: task.id,
      resourceId: resource.id,
      ...combineBreakdown(
        num(raw.total),
        num(raw.actualCost),
        baselineSnapshot === null ? null : num(baselineSnapshot.cost),
      ),
      timephased,
    });
  }

  // ---- Resource level ----------------------------------------------------
  const byResourceMap = new Map<
    string,
    { standardCost: number; overtimeCost: number; costPerUseTotal: number; cost: number; actualCost: number; baselineCost: number | null; hasBaseline: boolean }
  >();
  for (const assignment of project.assignments) {
    const resource = resourcesById.get(assignment.resourceId as string);
    if (resource === undefined) continue;
    if (resource.isBudget) continue;
    const task = tasksById.get(assignment.taskId as string);
    const calendar = task ? assignmentCalendarFor(task) : undefined;
    const raw = computeAssignmentCostRaw(assignment, resource, includeOvertime, task, statusDate, calendar);
    const key = resource.id as string;
    const acc = byResourceMap.get(key) ?? {
      standardCost: 0,
      overtimeCost: 0,
      costPerUseTotal: 0,
      cost: 0,
      actualCost: 0,
      baselineCost: baseline === undefined ? null : 0,
      hasBaseline: false,
    };
    acc.standardCost += num(raw.standardCost);
    acc.overtimeCost += num(raw.overtimeCost);
    acc.costPerUseTotal += num(raw.costPerUseTotal);
    acc.cost += num(raw.total);
    acc.actualCost += num(raw.actualCost);
    const baselineSnapshot = baselineAssignmentById?.get(assignment.id as string) ?? null;
    if (baselineSnapshot !== null) {
      acc.baselineCost = (acc.baselineCost ?? 0) + num(baselineSnapshot.cost);
      acc.hasBaseline = true;
    }
    byResourceMap.set(key, acc);
  }
  const byResource: ResourceCost[] = [...byResourceMap.entries()].map(([resourceId, acc]) => ({
    resourceId: resourceId as ResourceId,
    standardCost: money(acc.standardCost),
    overtimeCost: money(acc.overtimeCost),
    costPerUseTotal: money(acc.costPerUseTotal),
    ...combineBreakdown(acc.cost, acc.actualCost, baseline === undefined ? null : acc.baselineCost),
  }));

  // ---- Task level (own, before rollup) -----------------------------------
  interface OwnTaskAgg {
    resourceCost: number;
    resourceActualCost: number;
    fixedCost: number;
    fixedCostActual: number;
    baselineResourceCost: number | null;
  }
  const ownByTaskId = new Map<string, OwnTaskAgg>();
  for (const task of project.tasks) {
    ownByTaskId.set(task.id as string, {
      resourceCost: 0,
      resourceActualCost: 0,
      fixedCost: num(task.fixedCost),
      fixedCostActual: num(accrueFixedCostActual(task, statusDate)),
      baselineResourceCost: baseline === undefined ? null : 0,
    });
  }
  for (const ac of byAssignment) {
    const own = ownByTaskId.get(ac.taskId as string);
    if (own === undefined) continue;
    own.resourceCost += num(ac.cost);
    own.resourceActualCost += num(ac.actualCost);
    if (ac.baselineCost !== null) {
      own.baselineResourceCost = (own.baselineResourceCost ?? 0) + num(ac.baselineCost);
    }
  }

  const childrenByParentId = new Map<string, Task[]>();
  for (const task of project.tasks) {
    if (task.parentId === null) continue;
    const key = task.parentId as string;
    const list = childrenByParentId.get(key) ?? [];
    list.push(task);
    childrenByParentId.set(key, list);
  }

  interface RollupAgg {
    resourceCost: number;
    resourceActualCost: number;
    fixedCost: number;
    fixedCostActual: number;
    baselineCost: number | null;
  }
  const rollupCache = new Map<string, RollupAgg>();
  function rollup(taskId: string): RollupAgg {
    const cached = rollupCache.get(taskId);
    if (cached !== undefined) return cached;
    const own = ownByTaskId.get(taskId);
    const baselineTaskSnapshot = baselineTaskById?.get(taskId) ?? null;
    const agg: RollupAgg = {
      resourceCost: own?.resourceCost ?? 0,
      resourceActualCost: own?.resourceActualCost ?? 0,
      fixedCost: own?.fixedCost ?? 0,
      fixedCostActual: own?.fixedCostActual ?? 0,
      baselineCost: baseline === undefined ? null : 0,
    };
    const children = childrenByParentId.get(taskId) ?? [];
    if (children.length === 0) {
      // 葉節點：基準線比較直接用該任務的快照（若有）。
      agg.baselineCost = baselineTaskSnapshot === null ? null : num(baselineTaskSnapshot.cost);
    } else {
      let baselineSum: number | null = baseline === undefined ? null : 0;
      for (const child of children) {
        const r = rollup(child.id as string);
        agg.resourceCost += r.resourceCost;
        agg.resourceActualCost += r.resourceActualCost;
        agg.fixedCost += r.fixedCost;
        agg.fixedCostActual += r.fixedCostActual;
        if (baseline !== undefined) {
          baselineSum = (baselineSum ?? 0) + (r.baselineCost ?? 0);
        }
      }
      agg.baselineCost = baselineSum;
    }
    rollupCache.set(taskId, agg);
    return agg;
  }

  const byTask: TaskCost[] = project.tasks.map((task) => {
    const agg = rollup(task.id as string);
    const cost = agg.resourceCost + agg.fixedCost;
    const actualCost = agg.resourceActualCost + agg.fixedCostActual;
    return {
      taskId: task.id,
      fixedCost: money(agg.fixedCost),
      resourceCost: money(agg.resourceCost),
      ...combineBreakdown(cost, actualCost, agg.baselineCost),
    };
  });

  // ---- Project total: sum of top-level tasks (no double counting) -------
  const roots = project.tasks.filter((t) => t.parentId === null);
  const totalAgg = roots.reduce(
    (acc, t) => {
      const r = rollup(t.id as string);
      return {
        cost: acc.cost + r.resourceCost + r.fixedCost,
        actualCost: acc.actualCost + r.resourceActualCost + r.fixedCostActual,
        baselineCost: baseline === undefined ? null : (acc.baselineCost ?? 0) + (r.baselineCost ?? 0),
      };
    },
    { cost: 0, actualCost: 0, baselineCost: baseline === undefined ? null : 0 } as {
      cost: number;
      actualCost: number;
      baselineCost: number | null;
    },
  );
  const total = combineBreakdown(totalAgg.cost, totalAgg.actualCost, totalAgg.baselineCost);

  // ---- Cumulative curve ----------------------------------------------------
  let cumulativeCurve: TimephasedCostSeries = [];
  if (options?.timephasedGranularity !== undefined) {
    const allSeries: TimephasedCostSeries[] = [...assignmentTimephasedByTaskId.values()].flat();

    // 加上各任務 fixedCost 的逐時段分佈。
    for (const task of project.tasks) {
      if (num(task.fixedCost) === 0) continue;
      const calendarId = (task.calendarId ?? project.defaultCalendarId) as string;
      const calendar = resolveCal(calendarId);
      allSeries.push(
        distributeCost({
          start: task.start,
          finish: task.finish,
          totalCost: task.fixedCost,
          accrueAt: task.fixedCostAccrual,
          granularity: options.timephasedGranularity,
          calendar,
        }),
      );
    }

    const merged = mergeCostSeries(allSeries);
    // 依 start 分組加總同一切片的成本，再轉為累積序列。
    const byStart = new Map<string, { start: ISODateTime; end: ISODateTime; total: number }>();
    for (const slice of merged) {
      const key = slice.start as string;
      const existing = byStart.get(key);
      if (existing === undefined) {
        byStart.set(key, { start: slice.start, end: slice.end, total: num(slice.valueCost) });
      } else {
        existing.total += num(slice.valueCost);
        if (slice.end > existing.end) existing.end = slice.end;
      }
    }
    const orderedSlices = [...byStart.values()].sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
    let running = 0;
    cumulativeCurve = orderedSlices.map((s) => {
      running += s.total;
      return { kind: 'Cost' as const, start: s.start, end: s.end, valueCost: money(running) };
    });
  }

  return {
    total,
    byTask,
    byResource,
    byAssignment,
    cumulativeCurve,
    diagnostics,
  };
}

/** 計算實獲值分析結果。 */
export function computeEarnedValue(project: Project, options: EvmOptions): EvmResult {
  return computeEarnedValueImpl(project, options);
}
